<script setup>
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  applyRefusal,
  applySuggestion,
  insertRefusal,
  insertSuggestionAtCursor,
  replaceRefusal,
  replaceSelectionWithSuggestion,
} from '@/components/templateStudio/suggestionInsert.js'
import { useMarketOverviewAiDraft } from '@/composables/useMarketOverviewAiDraft.js'

// RED-317 §9 — the AI draft card above the draft's canvas. "Koosta uus AI-ga"
// asks for the data paragraphs of the period in the drawer's period field,
// written by the model from the cell's Maa-amet statistics alone (`trykis`,
// e.g. T13; no text of this overview is sent). The suggestion shows the whole
// new text: the AI paragraphs where the current version's data paragraphs
// were, every other part carried over from that version and marked "uuenda".
// The admin takes it in: "Võta ettepanek üle" (the whole suggestion, while the
// draft still is the current version), "Asenda valik" or "Lisa kursori
// kohale" (the AI paragraphs only), or "Kopeeri"; "Uuesti" asks again. Taken
// in, it is ordinary draft text (autosaved, published only with Avalda). A
// cell without a trykis shows the card disabled with the reason. Every
// outcome is inline; nothing the admin typed is ever touched except by the
// action they chose.
const props = defineProps({
  overviewId: { type: Number, required: true },
  // The MaRu table code of the cell's purpose; null = no AI draft for this cell.
  trykis: { type: String, default: null },
  period: { type: String, default: null },
  // The draft's Tiptap editor (OverviewCanvas), null while it mounts.
  editor: { type: Object, default: null },
})

const { t } = useI18n()
const ai = useMarketOverviewAiDraft()
const { state, suggestion, error } = ai

const working = computed(() => state.value === 'working')

// Re-read the editor's selection whenever it moves, for the two accept buttons.
const selectionTick = ref(0)
watch(
  () => props.editor,
  (editor, _previous, onCleanup) => {
    if (!editor) {
      return
    }
    const bump = () => selectionTick.value++
    editor.on('selectionUpdate', bump)
    editor.on('update', bump)
    onCleanup(() => {
      editor.off('selectionUpdate', bump)
      editor.off('update', bump)
    })
    bump()
  },
  { immediate: true },
)

const replaceBlocked = computed(() => {
  selectionTick.value
  return props.editor ? replaceRefusal(props.editor.state) : 'structure'
})
const insertBlocked = computed(() => {
  selectionTick.value
  return props.editor ? insertRefusal(props.editor.state) : 'structure'
})
// Null when the whole suggestion can be taken; 'none' without a current version, 'changed' once the draft is not it.
const applyBlocked = computed(() => {
  selectionTick.value
  return props.editor ? applyRefusal(props.editor.state, suggestion.value?.fingerprints) : 'none'
})
const canApply = computed(() => Array.isArray(suggestion.value?.fingerprints))

const TEXT_TYPES = ['paragraph', 'heading']
const blockLabel = type => (type === 'table' ? 'block_table' : ['bulletList', 'orderedList'].includes(type) ? 'block_list' : 'block_object')

/** The suggestion's parts as the card lists them: empty lines left out, a table, list or object named. */
const shownBlocks = computed(() =>
  (suggestion.value?.blocks ?? [])
    .filter(block => block.kind === 'ai' || block.text.trim() !== '' || !TEXT_TYPES.includes(block.type))
    .map(block => ({
      ...block,
      label: block.kind === 'carried' && !TEXT_TYPES.includes(block.type) ? t(`messages.admin.market_overviews.ai.${blockLabel(block.type)}`) : null,
    })),
)

/** What the line under the buttons says: the last acceptance, else what "Võta ettepanek üle" does or why it or Asenda valik waits. */
const taken = shallowRef(null) // null | 'all' | 'part'
const copied = ref(null) // null | 'ok' | 'failed'
let copiedTimer = null

const hint = computed(() => {
  if (copied.value === 'failed') {
    return { text: t('messages.admin.market_overviews.ai.copy_failed'), tone: 'text-red-600' }
  }
  if (taken.value) {
    return { text: t(`messages.admin.market_overviews.ai.${taken.value === 'all' ? 'taken_over' : 'inserted'}`), tone: 'text-green-700' }
  }
  if (canApply.value) {
    return applyBlocked.value === null
      ? { text: t('messages.admin.market_overviews.ai.hint_apply'), tone: 'text-gray-500' }
      : { text: t('messages.admin.market_overviews.ai.hint_changed'), tone: 'text-gray-500' }
  }
  if (replaceBlocked.value) {
    return { text: t(`messages.admin.market_overviews.ai.hint_${replaceBlocked.value}`), tone: 'text-gray-500' }
  }
  return null
})

const compose = () => {
  taken.value = null
  copied.value = null
  ai.draft(props.overviewId, props.period)
}

const accept = mode => {
  const paragraphs = suggestion.value?.paragraphs ?? []
  const done =
    mode === 'all'
      ? applySuggestion(props.editor, suggestion.value?.blocks, suggestion.value?.fingerprints)
      : mode === 'replace'
        ? replaceSelectionWithSuggestion(props.editor, paragraphs)
        : insertSuggestionAtCursor(props.editor, paragraphs)
  if (done) {
    taken.value = mode === 'all' ? 'all' : 'part'
    copied.value = null
    props.editor.commands.focus()
  }
}

const copy = async () => {
  clearTimeout(copiedTimer)
  try {
    await navigator.clipboard.writeText((suggestion.value?.paragraphs ?? []).join('\n\n'))
    copied.value = 'ok'
    copiedTimer = setTimeout(() => (copied.value = null), 2500)
  } catch {
    copied.value = 'failed'
  }
}

onBeforeUnmount(() => {
  clearTimeout(copiedTimer)
  ai.reset()
})

const errorText = computed(() => (error.value === true ? t('messages.admin.market_overviews.ai.error') : error.value))
</script>

<template>
  <section class="rounded-lg border px-3 py-2.5" :class="trykis ? 'border-gray-200 bg-white' : 'border-dashed border-gray-300 bg-gray-50'" data-testid="mo-ai" :data-disabled="trykis ? undefined : 'true'">
    <div class="flex flex-wrap items-end justify-between gap-3">
      <div class="min-w-0 flex-1">
        <h4 class="flex items-center gap-1.5 text-sm font-medium" :class="trykis ? 'text-gray-800' : 'text-gray-500'">
          <i class="pi pi-sparkles text-xs" :class="trykis ? 'text-primary-500' : 'text-gray-400'" />
          {{ $t('messages.admin.market_overviews.ai.title') }}
        </h4>
        <p class="mt-0.5 text-xs text-gray-500" data-testid="mo-ai-intro">
          {{ trykis ? $t('messages.admin.market_overviews.ai.intro', { report: trykis }) : $t('messages.admin.market_overviews.ai.disabled') }}
        </p>
      </div>
      <Button
        v-if="!suggestion"
        type="button"
        size="small"
        outlined
        icon="pi pi-sparkles"
        class="w-auto!"
        :label="working ? $t('messages.admin.market_overviews.ai.composing') : $t('messages.admin.market_overviews.ai.compose')"
        :loading="working"
        :disabled="!trykis || working"
        data-testid="mo-ai-compose"
        @click="compose"
      />
    </div>

    <Message v-if="errorText" severity="error" size="small" class="mt-2" data-testid="mo-ai-error">{{ errorText }}</Message>

    <div v-if="suggestion" class="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2" :class="{ 'opacity-60': working }" data-testid="mo-ai-suggestion">
      <p class="text-xs font-semibold text-gray-800">{{ $t('messages.admin.market_overviews.ai.suggestion') }}</p>
      <p v-if="canApply" class="mt-0.5 text-xs text-gray-500" data-testid="mo-ai-legend">{{ $t('messages.admin.market_overviews.ai.legend') }}</p>
      <div class="mt-1.5 flex max-h-96 flex-col gap-1 overflow-y-auto pr-1">
        <template v-for="(block, index) in shownBlocks" :key="index">
          <p v-if="block.kind === 'ai'" class="rounded border-l-4 border-sky-400 bg-sky-50 px-2 py-1 text-sm text-gray-800" data-testid="mo-ai-paragraph">
            <span class="mr-1.5 rounded bg-sky-600 px-1 py-px align-middle text-[0.6rem] font-semibold text-white">{{ $t('messages.admin.market_overviews.ai.tag_ai') }}</span>{{ block.text }}
          </p>
          <p
            v-else
            class="line-clamp-2 px-2 py-0.5 text-xs"
            :class="block.type === 'heading' ? 'font-semibold text-gray-700' : 'text-gray-500'"
            :data-update="block.update ? 'true' : undefined"
            data-testid="mo-ai-carried"
          >
            <span v-if="block.update" class="mr-1.5 rounded bg-amber-100 px-1 py-px align-middle text-[0.6rem] font-semibold text-amber-800" data-testid="mo-ai-update">{{
              $t('messages.admin.market_overviews.ai.tag_update')
            }}</span>
            <span v-if="block.label" class="italic">{{ block.label }}{{ block.text.trim() ? ': ' : '' }}</span>{{ block.text }}
          </p>
        </template>
      </div>
      <p class="mt-2 text-[0.7rem] text-gray-500" data-testid="mo-ai-source">{{ $t('messages.admin.market_overviews.ai.source', { report: suggestion.report, period: suggestion.period }) }}</p>
      <div class="mt-2 flex flex-wrap items-center gap-2">
        <Button type="button" size="small" text icon="pi pi-copy" class="w-auto!" :label="copied === 'ok' ? $t('messages.admin.market_overviews.ai.copied') : $t('messages.admin.market_overviews.ai.copy')" data-testid="mo-ai-copy" @click="copy" />
        <Button
          type="button"
          size="small"
          text
          icon="pi pi-refresh"
          class="w-auto!"
          :label="working ? $t('messages.admin.market_overviews.ai.composing') : $t('messages.admin.market_overviews.ai.again')"
          :loading="working"
          :disabled="working"
          data-testid="mo-ai-again"
          @click="compose"
        />
        <div class="ml-auto flex flex-wrap gap-2">
          <Button type="button" size="small" outlined class="w-auto!" :label="$t('messages.admin.market_overviews.ai.insert')" :disabled="working || insertBlocked !== null" data-testid="mo-ai-insert" @click="accept('insert')" />
          <Button
            type="button"
            size="small"
            :outlined="canApply"
            class="w-auto!"
            :label="$t('messages.admin.market_overviews.ai.replace')"
            :disabled="working || replaceBlocked !== null"
            data-testid="mo-ai-replace"
            @click="accept('replace')"
          />
          <Button v-if="canApply" type="button" size="small" class="w-auto!" :label="$t('messages.admin.market_overviews.ai.apply')" :disabled="working || applyBlocked !== null" data-testid="mo-ai-apply" @click="accept('all')" />
        </div>
      </div>
      <p class="mt-1.5 text-xs" :class="[hint?.tone ?? 'text-gray-500', { invisible: !hint }]" data-testid="mo-ai-hint">{{ hint?.text ?? '·' }}</p>
    </div>
  </section>
</template>
